import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  createWebhook,
  deleteWebhook,
  getWebhook,
  getWebhookEvents,
  getWebhookLog,
  getWebhookLogs,
  getWebhooks,
  updateWebhook
} from './api';
import {
  Webhook,
  WebhookEventsResponse,
  WebhookLog,
  WebhookLogsResponse,
  WebhookRequest,
  WebhooksResponse
} from './types';

export function useGetWebhooks(params?: Record<string, unknown>, options?: UseQueryOptions<WebhooksResponse, Error>) {
  return useQuery<WebhooksResponse>({
    queryKey: ['webhooks', params],
    queryFn: () => getWebhooks(params),
    ...options
  });
}

export function useGetWebhook(options?: UseQueryOptions<Webhook, Error>) {
  const { webhookId } = useParams();
  const isNumericId = Boolean(webhookId && /^\d+$/.test(webhookId));

  return useQuery<Webhook>({
    queryKey: ['webhook', webhookId],
    queryFn: () => getWebhook(webhookId!),
    enabled: isNumericId,
    ...options
  });
}

export function useGetWebhookEvents(options?: UseQueryOptions<WebhookEventsResponse, Error>) {
  return useQuery<WebhookEventsResponse>({
    queryKey: ['webhook-events'],
    queryFn: () => getWebhookEvents(),
    ...options
  });
}

export function useCreateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: WebhookRequest) => createWebhook(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    }
  });
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: WebhookRequest }) => updateWebhook(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      queryClient.invalidateQueries({ queryKey: ['webhook', String(data.id)] });
    }
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (webhookId: number) => deleteWebhook(webhookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    }
  });
}

export function useGetWebhookLogs(
  params?: Record<string, unknown>,
  options?: UseQueryOptions<WebhookLogsResponse, Error>
) {
  return useQuery<WebhookLogsResponse>({
    queryKey: ['webhook-logs', params],
    queryFn: () => getWebhookLogs(params),
    ...options
  });
}

export function useGetWebhookLog(options?: UseQueryOptions<WebhookLog, Error>) {
  const { logId } = useParams();

  return useQuery<WebhookLog>({
    queryKey: ['webhook-log', logId],
    queryFn: () => getWebhookLog(logId!),
    enabled: Boolean(logId),
    ...options
  });
}
